import { useMemo } from "react";

import { useAppSelector } from "@/hooks/redux";

import { ITeamStats } from "../types";
import { filterMembers, sortMembersByWorkStatus } from "../utils/teamUtils";

export const useTeamMembers = (searchQuery: string) => {
    const { id: managerId } = useAppSelector((state) => state.user);
    const { team, activeSchedule, routeList } = useAppSelector(
        (state) => state.sv,
    );

    const { activeUser, otherMembers } = useMemo(() => {
        if (!team?.members) {
            return { activeUser: null, otherMembers: [] };
        }

        const teamWithoutManager = team.members.filter(
            (m) => m.userId !== managerId,
        );

        const active = teamWithoutManager.find(
            (m) => m.userId === activeSchedule,
        );
        const others = teamWithoutManager.filter(
            (m) => m.userId !== activeSchedule,
        );

        return { activeUser: active || null, otherMembers: others };
    }, [team?.members, activeSchedule]);

    const filteredMembers = useMemo(() => {
        return filterMembers(otherMembers, searchQuery);
    }, [otherMembers, searchQuery]);

    const sortedMembers = useMemo(() => {
        return sortMembersByWorkStatus(filteredMembers);
    }, [filteredMembers]);

    const teamStats = useMemo((): ITeamStats => {
        const total = team?.members?.length || 0;
        const working = team?.members?.filter((m) => m.isWorking).length || 0;
        const notWorking = total - working;

        return { total, working, notWorking };
    }, [team?.members]);

    const teamRoutes = useMemo(() => {
        return Object.values(routeList).reduce(
            (acc, route) => ({ ...acc, [route.agentId]: route.uuid }),
            {} as { [agentId: string]: string },
        );
    }, [routeList?.routes]);

    return {
        activeUser,
        filteredMembers: sortedMembers,
        teamStats,
        teamRoutes,
    };
};
